import clsx from "clsx";

import type { Session, JobStatus } from "@/lib/types";

import { useStore } from "@/lib/store";
import { formatRelativeTime } from "@/lib/utils";

interface SidebarSessionItemProps {
  session: Session;
}

const statusColors: Record<JobStatus, string> = {
  pending: "bg-cyber-cyan",
  processing: "bg-cyber-magenta animate-neon-pulse",
  awaiting_response: "bg-cyber-magenta animate-neon-pulse",
  completed: "bg-cyber-green",
  cancelled: "bg-cyber-muted",
  failed: "bg-cyber-red",
};

export function SidebarSessionItem({ session }: SidebarSessionItemProps): JSX.Element {
  const activeSessionKey = useStore((state) => state.activeSessionKey);
  const selectSession = useStore((state) => state.selectSession);
  const closeSidebar = useStore((state) => state.closeSidebar);

  const isActive = activeSessionKey === session.threadKey;
  const title = session.sessionTitle ?? session.latestMessage.slice(0, 50);

  function handleClick(): void {
    selectSession(session.threadKey);
    closeSidebar();
  }

  return (
    <button
      onClick={handleClick}
      className={clsx(
        "relative w-full overflow-hidden rounded-none border-l-2 px-3 py-2.5 pr-8 text-left transition-all",
        isActive
          ? "border-cyber-cyan bg-cyber-surface"
          : "border-transparent hover:bg-cyber-surface/50",
      )}
    >
      <div className="flex items-center gap-2">
        {/* Title */}
        <span
          className={clsx(
            "flex-1 truncate whitespace-nowrap text-sm font-medium",
            isActive ? "text-cyber-text" : "text-cyber-text/80 hover:text-cyber-text",
          )}
        >
          {title}
        </span>
        {session.origin !== "interactive" && (
          <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1 py-0.5 text-[9px] uppercase tracking-wide text-amber-400">
            {session.origin}
          </span>
        )}
        {session.hidden && (
          <span className="rounded border border-cyber-border bg-cyber-surface/60 px-1 py-0.5 text-[9px] uppercase tracking-wide text-cyber-muted">
            Hidden
          </span>
        )}
      </div>
      {/* Status dot (hidden for completed) */}
      {session.latestStatus !== "completed" && (
        <span
          className={clsx(
            "absolute right-3 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full",
            statusColors[session.latestStatus],
          )}
          aria-hidden
        />
      )}
      <div className="mt-1 flex items-center gap-2 whitespace-nowrap text-[10px] text-cyber-muted/70">
        {session.userEmail && (
          <>
            <span className="max-w-[80px] truncate">{session.userEmail.split("@")[0]}</span>
            <span>·</span>
          </>
        )}
        <span className="uppercase">{session.source}</span>
        <span>·</span>
        <span>{formatRelativeTime(session.lastActivity)}</span>
      </div>
    </button>
  );
}
